import { getAccessToken, getActAs, authRefresh } from './auth'
import { getModelPref } from './modelPrefs'

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// ─── Auth-aware fetch wrapper ─────────────────────────────────────────────────
// Adds Authorization + X-Act-As headers to every request.
// On 401: tries one silent token refresh, then retries the request.

let _retryingRefresh = null  // singleton so parallel 401s share one refresh

export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`

  const token = getAccessToken()
  const actAs = getActAs()

  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` }   : {}),
    ...(actAs  ? { 'X-Act-As': actAs }                 : {}),
  }

  const res = await fetch(url, { ...options, headers })

  // Transparent token-refresh on 401
  if (res.status === 401 && token) {
    if (!_retryingRefresh) {
      _retryingRefresh = authRefresh().finally(() => { _retryingRefresh = null })
    }
    const refreshed = await _retryingRefresh
    if (refreshed?.access_token) {
      const retryHeaders = {
        ...options.headers,
        Authorization: `Bearer ${refreshed.access_token}`,
        ...(actAs ? { 'X-Act-As': actAs } : {}),
      }
      return fetch(url, { ...options, headers: retryHeaders })
    }
    // refresh failed — caller gets the original 401
  }

  return res
}

// ─── Health ───────────────────────────────────────────────────────────────────

export async function fetchHealth() {
  const res = await apiFetch('/api/health')
  if (!res.ok) throw new Error(`Health check failed: ${res.status}`)
  return res.json()
}

// ─── Profile ──────────────────────────────────────────────────────────────────

export async function saveProfile(user_id, {
  personality,
  competencies,
  career,
  motivation,
  evidence_data,
  style_analysis,
  tokens_used,
  cost_usd,
}) {
  const res = await apiFetch('/api/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id,
      personality_data: personality,
      competencies_data: competencies,
      career_data: career,
      motivation_data: motivation,
      evidence_data: evidence_data || null,
      style_analysis,
      tokens_used: tokens_used || 0,
      cost_usd: cost_usd || 0,
      run_timestamp: new Date().toISOString(),
    }),
  })
  if (!res.ok) throw new Error(`Save profile failed: ${res.status}`)
  return res.json()
}

export async function getProfile(user_id) {
  const res = await apiFetch(`/api/profile/${encodeURIComponent(user_id)}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Get profile failed: ${res.status}`)
  return res.json()
}

// ─── Chunks ───────────────────────────────────────────────────────────────────

export async function embedChunks(user_id, chunks) {
  if (!chunks?.length) return { embedded: 0 }
  console.log(`[API] embedChunks: sending ${chunks.length} chunks`)
  const res = await apiFetch('/api/embed-chunks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id, chunks }),
  })
  if (!res.ok) throw new Error(`Embed chunks failed: ${res.status}`)
  const result = await res.json()
  if (result.error) throw new Error(`Embed chunks error: ${result.error}`)
  console.log(`[API] embedChunks: done, embedded ${result.embedded}`)
  return result
}

export async function getChunksSample(userId = 'user', limit = 6) {
  const res = await apiFetch('/api/chunks/sample', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, limit }),
  })
  return res.json()
}

export async function getRepresentativeChunks(userId = 'user', n = 40, quotas = null) {
  const body = { user_id: userId, n }
  if (quotas) body.quotas = quotas
  const res = await apiFetch('/api/chunks/representative', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return res.json()
}

export async function getChunkSettings(userId) {
  const res = await apiFetch(`/api/chunk-settings/${encodeURIComponent(userId)}`)
  return res.json()
}

export async function saveChunkSettings(userId, settings) {
  const res = await apiFetch(`/api/chunk-settings/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings }),
  })
  return res.json()
}

export async function getChunksStats(userId) {
  const res = await apiFetch(`/api/chunks/stats/${encodeURIComponent(userId)}`)
  return res.json()
}

export async function clearChunks(userId) {
  const res = await apiFetch(`/api/chunks/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })
  return res.json()
}

// ─── Analysis ─────────────────────────────────────────────────────────────────

export async function deepAnalysis(userId = 'user') {
  const startRes = await apiFetch('/api/analyze/deep', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  const { job_id, error } = await startRes.json()
  if (error) return { error }
  if (!job_id) return { error: 'no job_id' }

  const deadline = Date.now() + 60 * 60 * 1000
  let networkErrors = 0
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 5000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      if (!poll.ok) { networkErrors++; if (networkErrors > 5) return { error: 'network error' }; continue }
      networkErrors = 0
      const job = await poll.json()
      if (job.status === 'done') return job.result
      if (job.status === 'error') return { error: job.error }
      if (job.status === 'not_found') return { error: 'job not found (server restarted?)' }
    } catch { networkErrors++; if (networkErrors > 5) return { error: 'network error' } }
  }
  return { error: 'timeout' }
}

export async function recalculate(userId, personalityModel = 'sonnet', socionicsModel = 'sonnet') {
  const res = await apiFetch('/api/analyze/recalculate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, personality_model: personalityModel, socionics_model: socionicsModel }),
  })
  if (!res.ok) throw new Error(`Сервер вернул ${res.status}. Попробуй ещё раз.`)
  return res.json()
}

// ─── Career ───────────────────────────────────────────────────────────────────

export async function careerRoles(userId = 'default') {
  const res = await apiFetch('/api/career/roles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), user_id: userId }),
  })
  return res.json()
}

export async function careerVacancies(query, userId = 'user', city = '', forceRefresh = false, { queries = [], page = 0 } = {}) {
  const startRes = await apiFetch('/api/career/vacancies/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), query, queries, page, user_id: userId, city, force_refresh: forceRefresh }),
  })
  const { job_id } = await startRes.json()
  // Poll until done (max 3 min)
  for (let i = 0; i < 45; i++) {
    await new Promise(r => setTimeout(r, 4000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      const j = await poll.json()
      if (j.status === 'done') return j.result
      if (j.status === 'error') return { error: j.error }
      if (j.status === 'not_found') return { error: 'job not found' }
    } catch {}
  }
  return { error: 'timeout' }
}

export async function scoreVacancies(vacancies, userId = 'user', roleQuery = '', city = '', forceRefresh = false, noCache = false) {
  const res = await apiFetch('/api/career/score-vacancies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), vacancies, user_id: userId, role_query: roleQuery, city, force_refresh: forceRefresh, no_cache: noCache }),
  })
  return res.json()
}

export async function careerMatch(vacancy, userId = 'default') {
  const res = await apiFetch('/api/career/match', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), vacancy, user_id: userId }),
  })
  return res.json()
}

export async function careerScoreRole(roleTitle, userId = 'user') {
  const res = await apiFetch('/api/career/score-role', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), role_title: roleTitle, user_id: userId }),
  })
  return res.json()
}

export async function analyzeVacancyByUrl(url, userId = 'user') {
  const res = await apiFetch('/api/career/vacancy-by-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), url, user_id: userId }),
  })
  return res.json()
}

export async function careerApply(vacancy, userId = 'user', { force = false } = {}) {
  const startRes = await apiFetch('/api/career/apply/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'), vacancy, user_id: userId, force }),
  })
  const { job_id } = await startRes.json()
  // Poll until done (max 5 min)
  for (let i = 0; i < 75; i++) {
    await new Promise(r => setTimeout(r, 4000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      const j = await poll.json()
      if (j.status === 'done') return j.result
      if (j.status === 'error') return { error: j.error }
      if (j.status === 'not_found') return { error: 'job not found' }
    } catch {}
  }
  return { error: 'timeout' }
}

export async function careerResume(userId = 'user', role = '') {
  const res = await apiFetch('/api/career/resume', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, role }),
  })
  return res.json()
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

// Raw login call — caller is responsible for calling setAuth() from auth.js
export async function authLogin(password) {
  const res = await fetch(`${BASE_URL}/api/auth`, {
    method: 'POST',
    credentials: 'include',  // needed to receive the dt_refresh cookie
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  return res.json()
}

export async function authParticipant(password, ownerUserId = 'user') {
  const res = await fetch(`${BASE_URL}/api/auth/participant`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, owner_user_id: ownerUserId }),
  })
  return res.json()
}

// ─── Bot / Chat ───────────────────────────────────────────────────────────────

// Public — no auth needed (visitor chat)
export async function botChat(visitorId, message, profileUserId = 'user') {
  const res = await fetch(`${BASE_URL}/api/bot/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visitor_id: visitorId, message, profile_user_id: profileUserId }),
  })
  return res.json()
}

export async function getWebConversations() {
  const res = await apiFetch('/api/bot/web-conversations')
  return res.json()
}

export async function getWebConversation(visitorId) {
  const res = await apiFetch(`/api/bot/web-conversations/${encodeURIComponent(visitorId)}`)
  return res.json()
}

// ─── Participants (СВОИ) ──────────────────────────────────────────────────────

export async function listParticipants(ownerUserId = 'user') {
  const res = await apiFetch(`/api/participants?owner_user_id=${encodeURIComponent(ownerUserId)}`)
  return res.json()
}

export async function saveParticipant(data) {
  const res = await apiFetch('/api/participants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return res.json()
}

export async function deleteParticipant(participantId) {
  const res = await apiFetch(`/api/participants/${participantId}`, { method: 'DELETE' })
  return res.json()
}

export async function mergeParticipants(targetId, sourceId, ownerUserId = 'user') {
  const res = await apiFetch(`/api/participants/${targetId}/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source_id: sourceId, owner_user_id: ownerUserId }),
  })
  return res.json()
}

export async function analyzeParticipant(participantId, context, ownerUserId = 'user') {
  const res = await apiFetch(`/api/participants/${participantId}/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ owner_user_id: ownerUserId, context }),
  })
  return res.json()
}

export async function getParticipantProfile(participantId) {
  const res = await apiFetch(`/api/participants/${participantId}/profile`)
  return res.json()
}

export async function createParticipantManual({ ownerUserId = 'user', displayName, isOwner = false }) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  const password = Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  const res = await apiFetch('/api/participants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      owner_user_id: ownerUserId,
      display_name: displayName,
      aliases: [displayName],
      password,
      message_count: 0,
      is_owner: isOwner,
    }),
  })
  const data = await res.json()
  if (data.error) throw new Error(data.error)
  return { ...data, password }
}

// ─── Natal chart ──────────────────────────────────────────────────────────────

export async function getNatal(userId = 'user') {
  const res = await apiFetch(`/api/natal/${userId}`)
  return res.json()
}

export async function saveBirthData(userId = 'user', birthData) {
  const res = await apiFetch('/api/natal/save-birth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, ...birthData }),
  })
  return res.json()
}

export async function geocodeCity(city) {
  const res = await apiFetch('/api/natal/geocode', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ city }),
  })
  return res.json()
}

export async function generateNatal(userId = 'user') {
  const res = await apiFetch('/api/natal/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  return res.json()
}

// ─── Threads ──────────────────────────────────────────────────────────────────

export async function generateThreadsPost(userId, topic, userContent = '') {
  const res = await apiFetch('/api/threads/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, topic, user_content: userContent }),
  })
  return res.json()
}

export async function startThreadsGenerate(userId, topic, mood, comment) {
  const res = await apiFetch('/api/threads/generate/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, topic, user_content: '', mood, comment }),
  })
  const data = await res.json()
  if (data.error) throw new Error(data.error)
  if (!data.job_id) throw new Error('no job_id')
  return data.job_id
}

export async function startThreadsSuggestTopic(userId) {
  const res = await apiFetch('/api/threads/suggest-topic/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  const data = await res.json()
  if (data.error) throw new Error(data.error)
  if (!data.job_id) throw new Error('no job_id')
  return data.job_id
}

// Generic poller for /api/analyze/job/{job_id} — feeds the FULL job payload (stages, critic_note, etc)
// to onProgress on every tick, resolves with job.result once status === 'done'.
export async function pollThreadsJob(jobId, onProgress, timeoutMs = 180000) {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    await new Promise(r => setTimeout(r, 1500))
    try {
      const res = await fetch(`${BASE_URL}/api/analyze/job/${jobId}`)
      if (!res.ok) continue
      const data = await res.json()
      if (onProgress) onProgress(data)
      if (data.status === 'done') return data.result
      if (data.status === 'error') throw new Error(data.error || 'Ошибка генерации')
      if (data.status === 'not_found') throw new Error('Job not found')
    } catch (e) {
      if (e.message && (e.message.includes('Ошибка') || e.message.includes('Job'))) throw e
    }
  }
  throw new Error('Таймаут генерации')
}

// ─── Facts ────────────────────────────────────────────────────────────────────

// Returns job_id immediately — let caller poll /api/analyze/job/{job_id} for progress
export async function extractFactsStart(userId) {
  const startRes = await apiFetch('/api/facts/extract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  const data = await startRes.json()
  if (data.error) throw new Error(data.error)
  if (!data.job_id) throw new Error('no job_id')
  return data.job_id
}

// Legacy: blocking version (kept for backward compat)
export async function extractFacts(userId) {
  const startRes = await apiFetch('/api/facts/extract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  const { job_id, error } = await startRes.json()
  if (error) return { error }
  if (!job_id) return { error: 'no job_id' }

  const deadline = Date.now() + 30 * 60 * 1000
  let networkErrors = 0
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 4000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      if (!poll.ok) { networkErrors++; if (networkErrors > 5) return { error: 'network error' }; continue }
      networkErrors = 0
      const job = await poll.json()
      if (job.status === 'done') return job.result
      if (job.status === 'error') return { error: job.error }
      if (job.status === 'not_found') return { error: 'job not found (server restarted?)' }
    } catch { networkErrors++; if (networkErrors > 5) return { error: 'network error' } }
  }
  return { error: 'timeout' }
}

export async function getFacts(userId) {
  const res = await apiFetch(`/api/facts/${userId}`)
  return res.json()
}

export async function deleteFact(userId, factId) {
  const res = await apiFetch(`/api/facts/${userId}/fact/${factId}`, {
    method: 'DELETE',
  })
  return res.json()
}

export async function deleteAllFacts(userId) {
  const res = await apiFetch(`/api/facts/${userId}`, { method: 'DELETE' })
  return res.json()
}

export async function setFactVerdict(userId, factId, verdict) {
  const res = await apiFetch(`/api/facts/${userId}/fact/${factId}/verdict`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verdict }),
  })
  return res.json()
}

// ─── Fact contradictions ────────────────────────────────────────────────────

export async function getFactContradictions(userId) {
  const res = await apiFetch(`/api/facts/contradictions/${userId}`)
  return res.json()
}

export async function dismissContradiction(contradictionId) {
  const res = await apiFetch(`/api/facts/contradictions/${contradictionId}/dismiss`, {
    method: 'PATCH',
  })
  return res.json()
}

// Manual/temporary trigger — normal flow runs this automatically after extraction.
export async function startContradictionDetection(userId, model = 'sonnet') {
  const res = await apiFetch('/api/facts/detect-contradictions/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, model }),
  })
  const data = await res.json()
  if (data.error) throw new Error(data.error)
  if (!data.job_id) throw new Error('no job_id')
  return data.job_id
}

export async function getVoiceNotes(userId) {
  const res = await apiFetch(`/api/voice-notes/${userId}`)
  return res.json()
}

export async function setVoiceNoteVerdict(userId, noteId, verdict) {
  const res = await apiFetch(`/api/voice-notes/${userId}/note/${noteId}/verdict`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verdict }),
  })
  return res.json()
}

export async function getFactsExtractionStatus(userId) {
  const res = await apiFetch(`/api/facts/extraction-status/${userId}`)
  return res.json()
}

export async function extractFactsIncremental(userId) {
  const startRes = await apiFetch('/api/facts/extract-incremental', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  const { job_id, error } = await startRes.json()
  if (error) return { error }
  if (!job_id) return { error: 'no job_id' }
  const deadline = Date.now() + 60 * 60 * 1000
  let networkErrors = 0
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 5000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      if (!poll.ok) { networkErrors++; if (networkErrors > 5) return { error: 'network' }; continue }
      networkErrors = 0
      const job = await poll.json()
      if (job.status === 'done') return job.result || {}
      if (job.status === 'error') return { error: job.error }
      if (job.status === 'not_found') return { error: 'job not found' }
    } catch { networkErrors++; if (networkErrors > 5) return { error: 'network' } }
  }
  return { error: 'timeout' }
}

export async function getSurveyStatusFull(userId) {
  const res = await apiFetch(`/api/survey/status/${userId}`)
  return res.json()
}

export async function generateSurveySession(userId, sessionNum) {
  const startRes = await apiFetch(`/api/survey/generate-group2/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session: sessionNum }),
  })
  const { job_id, questions, error } = await startRes.json()
  if (questions) return { questions }
  if (error) return { error }
  if (!job_id) return { error: 'no job_id' }
  const deadline = Date.now() + 10 * 60 * 1000
  let networkErrors = 0
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 3000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      if (!poll.ok) { networkErrors++; if (networkErrors > 3) return { error: 'network' }; continue }
      networkErrors = 0
      const job = await poll.json()
      if (job.status === 'done') return job.result || {}
      if (job.status === 'error') return { error: job.error }
    } catch { networkErrors++ }
  }
  return { error: 'timeout' }
}

// extractKnowledge (/api/extract, snapshot Haiku path) retired — see
// server/archive/legacy_haiku_fact_extraction.py. Use extractFacts/extractFactsStart.

// ─── Published Resumes ────────────────────────────────────────────────────────

export async function publishResume(userId, vacancy, content) {
  const res = await apiFetch('/api/resume/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, vacancy, content }),
  })
  if (!res.ok) throw new Error(`Publish failed: ${res.status}`)
  return res.json()
}

export async function getPublishedResumes(userId) {
  const res = await apiFetch(`/api/resume/list/${encodeURIComponent(userId)}`)
  if (!res.ok) throw new Error(`List failed: ${res.status}`)
  return res.json()
}

export async function getPublishedResumeBySlug(slug) {
  // apiFetch: владелец получает полную версию (нужна для редактирования), гость — публичную
  const res = await apiFetch(`/api/resume/slug/${encodeURIComponent(slug)}`)
  if (!res.ok) throw new Error(`Not found: ${res.status}`)
  return res.json()
}

export async function updatePublishedResume(slug, content) {
  const res = await apiFetch(`/api/resume/slug/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  })
  if (!res.ok) throw new Error(`Update failed: ${res.status}`)
  return res.json()
}

// Public — fire and forget, no auth
export async function recordResumeVisit(slug) {
  const referer = document.referrer || ''
  const user_agent = navigator.userAgent || ''
  try {
    await fetch(`${BASE_URL}/api/resume/slug/${encodeURIComponent(slug)}/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referer, user_agent }),
    })
  } catch {}
}

export async function deletePublishedResume(slug, userId) {
  const res = await apiFetch(`/api/resume/slug/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`)
  return res.json()
}

// ─── Public participant pages ─────────────────────────────────────────────────

export async function getPublicParticipant(participantId) {
  const res = await fetch(`${BASE_URL}/api/public/participant/${participantId}`)
  if (!res.ok) throw new Error(`Not found: ${res.status}`)
  return res.json()
}

export async function updateParticipantVisibility(participantId, publicSettings) {
  const res = await apiFetch(`/api/participants/${participantId}/visibility`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ public_settings: publicSettings }),
  })
  if (!res.ok) throw new Error(`Update failed: ${res.status}`)
  return res.json()
}

export async function saveOwnerPublicSettings(userId, publicSettings) {
  const res = await apiFetch('/api/profile/public-settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, public_settings: publicSettings }),
  })
  if (!res.ok) throw new Error(`Update failed: ${res.status}`)
  return res.json()
}

// ─── Development module v2 (RPG Skill Tree) ──────────────────────────────────

export const getDevTip = userId =>
  apiFetch(`/api/dev/tip/${userId}`).then(r => r.json()).catch(() => ({ tip: '' }))

export const generateDevTracks = userId =>
  apiFetch(`/api/dev/generate-tracks/${userId}`, { method: 'POST' }).then(r => r.json())

export const getDevTracks = userId =>
  apiFetch(`/api/dev/tracks/${userId}`).then(r => r.json()).catch(() => ({ tracks: [] }))

export const selectDevTrack = (trackId, selected = true) =>
  apiFetch(`/api/dev/tracks/${trackId}/select`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selected })
  }).then(r => r.json())

export const getDevTree = userId =>
  apiFetch(`/api/dev/tree/${userId}`).then(r => r.json()).catch(() => ({ tracks: [] }))

export const startDevSkill = (skillId, userId) =>
  apiFetch(`/api/dev/skills/${skillId}/start`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId })
  }).then(r => r.json())

export const updateDevTask = (taskId, data) =>
  apiFetch(`/api/dev/tasks/${taskId}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
  }).then(r => r.json())

export const checkDevTask = (taskId, answer, userId) =>
  apiFetch(`/api/dev/tasks/${taskId}/check`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer, user_id: userId })
  }).then(r => r.json())

export const getDevProgress = userId =>
  apiFetch(`/api/dev/progress/${userId}`).then(r => r.json()).catch(() => ({ bonuses: {}, mastered_skills: 0 }))

export const deleteDevTree = userId =>
  apiFetch(`/api/dev/tree/${userId}`, { method: 'DELETE' }).then(r => r.json())

// ─── Survey (Анкетирование) ───────────────────────────────────────────────────

export const getSurveyStatus = userId =>
  apiFetch(`/api/survey/status/${encodeURIComponent(userId)}`)
    .then(r => r.json()).catch(() => ({ group1_done: false, group2_done: false }))

export async function clearSurvey(userId) {
  const res = await apiFetch(`/api/survey/${encodeURIComponent(userId)}`, { method: 'DELETE' })
  return res.json()
}

// Public — no auth (group1 is the first thing a new user sees)
export const getSurveyGroup1Questions = () =>
  fetch(`${BASE_URL}/api/survey/questions/group1`).then(r => r.json())

export async function generateSurveyGroup2(userId) {
  const startRes = await apiFetch(`/api/survey/generate-group2/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const data = await startRes.json()
  if (data.questions) return data
  if (!data.job_id) return { error: data.error || 'no job_id' }
  const deadline = Date.now() + 5 * 60 * 1000
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 2000))
    const poll = await apiFetch(`/api/analyze/job/${data.job_id}`)
    if (!poll.ok) continue
    const job = await poll.json()
    if (job.status === 'done') return job.result
    if (job.status === 'error') return { error: job.error }
  }
  return { error: 'timeout generating group 2' }
}

export async function submitSurvey(userId, group, responses, sessionNum = 1) {
  const res = await apiFetch('/api/survey/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, group, responses, session_num: sessionNum }),
  })
  return res.json()
}

export const getSurveyResponses = userId =>
  apiFetch(`/api/survey/responses/${encodeURIComponent(userId)}`)
    .then(r => r.json()).catch(() => ({ responses: [], total: 0 }))

export async function analyzeSurvey(userId) {
  const startRes = await apiFetch(`/api/survey/analyze/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  const { job_id, error } = await startRes.json()
  if (error) return { error }
  if (!job_id) return { error: 'no job_id' }
  const deadline = Date.now() + 11 * 60 * 1000
  let netErrors = 0
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 4000))
    try {
      const poll = await apiFetch(`/api/analyze/job/${job_id}`)
      if (!poll.ok) { netErrors++; if (netErrors > 5) return { error: 'network error' }; continue }
      netErrors = 0
      const job = await poll.json()
      if (job.status === 'done') return job.result
      if (job.status === 'error') return { error: job.error }
      if (job.status === 'not_found') return { error: 'server restarted, try again' }
    } catch { netErrors++; if (netErrors > 5) return { error: 'network error' } }
  }
  return { error: 'timeout' }
}

// ─── Invites ──────────────────────────────────────────────────────────────────

export async function createInvite(note = '', expiresDays = 7) {
  const res = await apiFetch('/api/invites/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note, expires_days: expiresDays }),
  })
  if (!res.ok) throw new Error(`Create invite failed: ${res.status}`)
  return res.json()  // { ok, token, invite_url, id }
}

export async function validateInvite(token) {
  const res = await fetch(`${BASE_URL}/api/invites/${encodeURIComponent(token)}`)
  return res.json()  // { ok, token, note, expires_at } or { error }
}

// Public — no auth (registration via invite)
export async function registerWithInvite(token, displayName, password) {
  const res = await fetch(`${BASE_URL}/api/invites/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, display_name: displayName, password }),
  })
  return res.json()  // { ok, access_token, user_id, role, participant_id, display_name }
}

// Google OAuth — login or register via invite
export async function authGoogle(idToken) {
  const res = await fetch(`${BASE_URL}/api/auth/google`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  })
  return res.json()  // { ok, access_token, user_id, role, ... } or { ok: false, error }
}

// Register via invite using Google ID token (no password needed)
export async function registerWithInviteGoogle(token, idToken) {
  const res = await fetch(`${BASE_URL}/api/invites/register`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, google_id_token: idToken }),
  })
  return res.json()
}

// ─── Socionics ────────────────────────────────────────────────────────────────

export async function analyzeSocionics(userId, model = 'sonnet') {
  const res = await apiFetch('/api/socionics/analyze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, model }),
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok || d.error) throw new Error(d.error || `Сервер вернул ${res.status}`)
  return d
}

// ─── Physiognomy (Физиогномика) ────────────────────────────────────────────────

export async function getPhysiognomy(userId) {
  const res = await fetch(`${BASE_URL}/api/physiognomy/${userId}`)
  if (!res.ok) throw new Error(`get-physiognomy failed: ${res.status}`)
  return res.json()
}

export async function uploadPhysiognomyPhoto(userId, photoDataUrl) {
  const res = await apiFetch(`/api/physiognomy/upload/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ photo_b64: photoDataUrl }),
  })
  const d = await res.json().catch(() => ({}))
  if (!res.ok || d.error) throw new Error(d.error || `Сервер вернул ${res.status}`)
  return d
}

// Returns job_id immediately — caller polls /api/analyze/job/{job_id} via pollThreadsJob
export async function analyzePhysiognomyStart(userId, model = 'sonnet') {
  const res = await apiFetch(`/api/physiognomy/analyze/start/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model }),
  })
  const d = await res.json().catch(() => ({}))
  if (d.error) throw new Error(d.error)
  if (!d.job_id) throw new Error('no job_id')
  return d.job_id
}

// ─── Music (Музыка) ─────────────────────────────────────────────────────────

export async function addMusicPlaylistStart(userId, url) {
  const res = await apiFetch('/api/music/playlist/add/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, url }),
  })
  const d = await res.json().catch(() => ({}))
  if (d.error) throw new Error(d.error)
  if (!d.job_id) throw new Error('no job_id')
  return d.job_id
}

export async function getMusicPlaylists(userId) {
  const res = await fetch(`${BASE_URL}/api/music/playlists/${userId}`)
  if (!res.ok) throw new Error(`get-music-playlists failed: ${res.status}`)
  return res.json()
}

export async function deleteMusicPlaylist(playlistId) {
  const res = await apiFetch(`/api/music/playlist/${playlistId}`, { method: 'DELETE' })
  const d = await res.json().catch(() => ({}))
  if (!res.ok || d.error) throw new Error(d.error || `Сервер вернул ${res.status}`)
  return d
}

export async function getMusic(userId) {
  const res = await fetch(`${BASE_URL}/api/music/${userId}`)
  if (!res.ok) throw new Error(`get-music failed: ${res.status}`)
  return res.json()
}

// Pure-SQL genre/artist breakdown — no LLM call, cheap, instant.
export async function getMusicTaste(userId) {
  const res = await fetch(`${BASE_URL}/api/music/taste/${userId}`)
  if (!res.ok) throw new Error(`get-music-taste failed: ${res.status}`)
  return res.json()
}

// compareUserId is optional — omit for a standalone blind read with no comparison.
// sampleSize is optional — how many stored tracks go into the LLM prompt (server default 300).
export async function analyzeMusicStart(userId, model = 'sonnet', compareUserId = null, sampleSize = null) {
  const res = await apiFetch('/api/music/analyze/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, model, compare_user_id: compareUserId, sample_size: sampleSize }),
  })
  const d = await res.json().catch(() => ({}))
  if (d.error) throw new Error(d.error)
  if (!d.job_id) throw new Error('no job_id')
  return d.job_id
}

export async function musicCompatibilityStart(userIdA, userIdB, model = 'sonnet') {
  const res = await apiFetch('/api/music/compatibility/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id_a: userIdA, user_id_b: userIdB, model }),
  })
  const d = await res.json().catch(() => ({}))
  if (d.error) throw new Error(d.error)
  if (!d.job_id) throw new Error('no job_id')
  return d.job_id
}

export async function musicCrossRecommendStart(fromUserId, toUserId, model = 'sonnet') {
  const res = await apiFetch('/api/music/cross-recommend/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from_user_id: fromUserId, to_user_id: toUserId, model }),
  })
  const d = await res.json().catch(() => ({}))
  if (d.error) throw new Error(d.error)
  if (!d.job_id) throw new Error('no job_id')
  return d.job_id
}

// Used by MusicTab to build the "compare with / recommend to" target list — a GET, so it's
// unauthenticated by the JWT middleware, same as everywhere else this endpoint is called.
export async function getParticipantsList(ownerUserId = 'user') {
  const res = await fetch(`${BASE_URL}/api/participants?owner_user_id=${encodeURIComponent(ownerUserId)}`)
  if (!res.ok) throw new Error(`get-participants failed: ${res.status}`)
  const d = await res.json()
  return Array.isArray(d) ? d : []
}

export async function createParticipantFromPlaylistStart(ownerUserId, displayName, url, model = 'sonnet') {
  const res = await apiFetch('/api/participants/from-playlist', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ owner_user_id: ownerUserId, display_name: displayName, url, model }),
  })
  const d = await res.json().catch(() => ({}))
  if (d.error) throw new Error(d.error)
  if (!d.job_id) throw new Error('no job_id')
  return d.job_id
}

// ─── Development section — stats & avatar ────────────────────────────────────

export async function evaluateDevStats(userId) {
  const res = await apiFetch(`/api/dev/evaluate-stats/${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  if (!res.ok) throw new Error(`evaluate-stats failed: ${res.status}`)
  return res.json()
}

export async function getDevStats(userId) {
  const res = await fetch(`${BASE_URL}/api/dev/stats/${userId}`)
  if (!res.ok) throw new Error(`get-stats failed: ${res.status}`)
  return res.json()
}

export async function getAvatar(userId) {
  const res = await fetch(`${BASE_URL}/api/avatar/${userId}`)
  if (!res.ok) throw new Error(`get-avatar failed: ${res.status}`)
  return res.json()  // { lineart: "data:image/png;base64,..." | null }
}

export async function uploadAvatar(userId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const res = await apiFetch(`/api/avatar/upload/${userId}`, {
    method: 'POST',
    body: formData,
    // no Content-Type header — browser sets it with boundary for FormData
  })
  if (!res.ok) throw new Error(`upload-avatar failed: ${res.status}`)
  return res.json()  // { ok, lineart }
}

// Create invite linked to an existing participant (for "Свои" card)
export async function createInviteForParticipant(participantId, note = '') {
  const res = await apiFetch('/api/invites/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      note: note || `Инвайт для участника`,
      expires_days: 30,
      linked_participant_id: participantId,
    }),
  })
  if (!res.ok) throw new Error(`Create invite failed: ${res.status}`)
  return res.json()  // { ok, token, invite_url, id }
}

// ─── Mentor (наставник) ───────────────────────────────────────────────────

export async function createMentorSession(userId, role, title = null) {
  const res = await apiFetch('/api/mentor/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, role, title }),
  })
  if (!res.ok) throw new Error(`createMentorSession failed: ${res.status}`)
  return res.json()
}

export async function listMentorSessions(userId) {
  const res = await fetch(`${BASE_URL}/api/mentor/sessions/${userId}`)
  if (!res.ok) throw new Error(`listMentorSessions failed: ${res.status}`)
  return res.json()
}

export async function getMentorSession(sessionId) {
  const res = await fetch(`${BASE_URL}/api/mentor/session/${sessionId}`)
  if (!res.ok) throw new Error(`getMentorSession failed: ${res.status}`)
  return res.json()
}

export async function deleteMentorSession(sessionId) {
  const res = await apiFetch(`/api/mentor/session/${sessionId}`, { method: 'DELETE' })
  if (!res.ok) throw new Error(`deleteMentorSession failed: ${res.status}`)
  return res.json()
}

export async function mentorChatStart(sessionId, userId, role, message, fileContent = null) {
  const res = await apiFetch('/api/mentor/chat/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, user_id: userId, role, message, file_content: fileContent }),
  })
  if (!res.ok) throw new Error(`mentorChatStart failed: ${res.status}`)
  return res.json() // { job_id }
}

export async function mentorCouncilStart(userId, question, fileContent = null, sessionId = null) {
  const res = await apiFetch('/api/mentor/council/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, question, file_content: fileContent, session_id: sessionId }),
  })
  if (!res.ok) throw new Error(`mentorCouncilStart failed: ${res.status}`)
  return res.json() // { job_id, session_id }
}

export async function mentorExtractFile(file) {
  const form = new FormData()
  form.append('file', file)
  const res = await apiFetch('/api/mentor/extract-file', { method: 'POST', body: form })
  if (!res.ok) throw new Error(`mentorExtractFile failed: ${res.status}`)
  return res.json() // { text, filename, chars }
}

// Poll job for mentor (reuses existing pollJob pattern)
export async function pollMentorJob(jobId, onProgress, timeoutMs = 600000) {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    await new Promise(r => setTimeout(r, 2000))
    try {
      const res = await fetch(`${API}/api/analyze/job/${jobId}`)
      if (!res.ok) continue
      const data = await res.json()
      if (onProgress) onProgress(data)
      if (data.status === 'done') return data.result
      if (data.status === 'error') throw new Error(data.error || 'Job failed')
      if (data.status === 'not_found') throw new Error('Job not found')
    } catch (e) {
      if (e.message && e.message.includes('Job')) throw e
    }
  }
  throw new Error('Mentor job timeout')
}

// ─── Очередь откликов (полуавтомат) ───────────────────────────────────────────

export async function runApplyQueue(userId, roles, { limit = 3, minScore = 60 } = {}) {
  const res = await apiFetch('/api/apply-queue/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model: getModelPref('career'),
      user_id: userId, limit, min_score: minScore,
      roles: roles.map(r => ({ title: r.title, hh_query: r.hh_query, hh_queries: r.hh_queries || [] })),
    }),
  })
  if (!res.ok) throw new Error(`Не удалось запустить подбор: ${res.status}`)
  return (await res.json()).job_id
}

export async function getApplyQueue(userId) {
  const res = await apiFetch(`/api/apply-queue/${encodeURIComponent(userId)}`)
  if (!res.ok) throw new Error(`Очередь недоступна: ${res.status}`)
  return res.json()  // { items, spent_today, daily_cap }
}

export async function updateApplyQueueItem(id, patch) {
  const res = await apiFetch(`/api/apply-queue/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) throw new Error(`Не удалось сохранить: ${res.status}`)
}

export async function deleteApplyQueueItem(id) {
  const res = await apiFetch(`/api/apply-queue/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error(`Не удалось удалить: ${res.status}`)
}
